import {Component, ElementRef, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {DefaultFilter} from '@mominsamir/ngx-smart-table';
import {debounceTime, distinctUntilChanged, filter, map, take, takeUntil} from 'rxjs/operators';
import {BehaviorSubject, ReplaySubject, Subject} from 'rxjs';
import {GeneralOption} from '@core/interfaces/system/system-common';
import Timeout = NodeJS.Timeout;

// TODO: This component is overly complicated and difficult to maintain.
// It should be refactored and possibly split into two components as part of tech debt work.

@Component({
    template: `
        <div
            class="form-container"
            #buttonTarget
            (click)="handleClick()"
            ngxClickOutside
            (clickOutside)="handleClickOutside($event)"
        >
            <nb-select fullWidth multiple [formControl]="selectControl" [placeholder]="placeholder">
                <nb-select-label>
                    {{ isAllSelected ? 'All' : getLabels(selectControl.value) }}
                </nb-select-label>
                <nb-option [value]="''" class="search" (click)="handleOptionClick($event, null)">
                    <div class="search-overlay" *ngIf="isLoading$ | async" [nbSpinner]="true"></div>
                    <nb-form-field>
                        <input
                            #filterInput
                            nbInput
                            fullWidth
                            [formControl]="filterControl"
                            (keydown.space)="$event.stopImmediatePropagation()"
                            placeholder="Search..."
                            [ngxOnlyNumber]="onlyNumbersSearch"
                            [strict]="onlyNumbersStrictMode"
                        />
                        <button nbSuffix nbButton ghost (click)="this.filterControl.reset()">
                            <nb-icon [icon]="'close'" pack="eva" [attr.aria-label]="'clear search field'"></nb-icon>
                        </button>
                    </nb-form-field>
                </nb-option>
                <nb-option
                    *ngIf="options.length && options.length === (filteredOptions$ | async).length && !dataFunction"
                    [value]="0"
                    (click)="toggleSelectAll($event)"
                    [class.pointer-none]="isLoading$ | async"
                >
                    Select All
                </nb-option>
                <nb-option
                    *ngFor="let option of filteredOptions$ | async"
                    [value]="option.value"
                    (click)="handleOptionClick($event, option)"
                    [class.pointer-none]="isLoading$ | async"
                >
                    {{ option.label }}
                </nb-option>
            </nb-select>
            <span class="amount" *ngIf="selectControl?.value?.length">
                {{ getLengthSelected(selectControl.value) }}
            </span>
        </div>
    `,
    styleUrls: ['./multi-select-filter.component.scss'],
})
export class MultiSelectWithSearchFilterComponent extends DefaultFilter implements OnInit, OnChanges, OnDestroy {
    @ViewChild('filterInput') filterInput: ElementRef;
    @ViewChild('buttonTarget') buttonTarget: any;

    delay = 500;
    searchDelay = 200;
    options: {value: string; label: string}[];
    placeholder: string;
    dataFunction: (fieldKey: string, searchString: string) => any;
    onlyNumbersSearch: false;
    onlyNumbersStrictMode: false;
    fieldName: string;
    private filterTimeoutId: Timeout;

    selectControl: FormControl = new FormControl([]);
    filterControl: FormControl = new FormControl();
    filteredOptions$: ReplaySubject<{value: string; label: string}[]> = new ReplaySubject(1);

    isAllSelected: boolean = false;
    isOpen = false;
    isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    selectedOptions = [];

    _onDestroy = new Subject();

    constructor() {
        super();
    }

    ngOnInit() {
        this.selectControl.valueChanges
            .pipe(
                takeUntil(this._onDestroy),
                filter((value) => {
                    //when focusing on search input checkbox selects
                    //this removes search-input's value from this.selectControl
                    // and stops the process of subscription to this event
                    let isSearch = value.includes('');
                    if (isSearch) {
                        let _value = value.filter((item) => item !== '');
                        this.selectControl.setValue(_value, {emitEvent: false});
                    }

                    return !isSearch;
                }),
                debounceTime(this.delay),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                map((value: any[]) => {
                    if (value && value.length === this.options.length && !value.includes(0)) {
                        let _value = [...value, 0];
                        this.isAllSelected = true;
                        this.selectControl.setValue(_value, {emitEvent: false});
                        return _value;
                    } else if (value && value.length !== this.options.length + 1) {
                        let _value = value.filter((item) => item !== 0);
                        this.isAllSelected = false;
                        this.selectControl.setValue(_value, {emitEvent: false});
                        return _value;
                    } else return value;
                }),
            )
            .subscribe((value: any[]) => {
                if (!value || !value.length) {
                    this.query = '';
                } else if (this.selectControl.status === 'VALID') {
                    let result = '';
                    value.forEach((item) => {
                        if (item) {
                            if (result === '') result += `${item}`;
                            else result += `|${item}`;
                        }
                    });

                    this.query = result;
                }

                // If the dropdown is already closed but the FormControl is still updating, update the filter as well.
                // Normally, setFilter() is triggered in handleClickOutside.
                // When quickly selecting and closing the dropdown, the FormControl may update after the dropdown has closed.
                // To avoid increasing the timeout delay in handleClickOutside (which would be noticeable to the user),
                // handle the delayed FormControl update here and update the filter if necessary.
                if (!this.isOpen) {
                    this.setFilter();
                }
            });

        this.options = this.column.filter.config?.options || [];
        this.dataFunction = this.column.filter.config?.dataFunction || null;
        this.fieldName = this.column.filter.config?.fieldName || '';
        this.placeholder = this.column.filter.config?.placeholder || 'Select...';
        this.onlyNumbersSearch = this.column.filter.config?.onlyNumbersSearch || false;
        this.onlyNumbersStrictMode = this.column.filter.config?.onlyNumbersStrictMode || false;

        this.filterControl.valueChanges
            .pipe(takeUntil(this._onDestroy), distinctUntilChanged(), debounceTime(this.searchDelay))
            .subscribe((value: string) => {
                this.selectControl.reset([]);
                this.filterOptions();
                this.filterInput.nativeElement.focus();
            });

        if (!this.dataFunction) {
            this.filteredOptions$.next(this.options);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.query) {
            this.query = changes.query.currentValue;
            this.selectControl.setValue(this.selectControl.value);
        }
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    handleClick(): void {
        if (this.isOpen) {
            /**
             * When quickly selecting and unselecting different options, fields can be unexpectedly set or cleared against the user's intentions
             * therefore, we need to set and clear a global debouncing timer to ensure that we only call setFilter after
             * the timer has completed without additional inputs. This prevents multiple method calls in a short span of time.
             */
            if (!!this.filterTimeoutId) clearTimeout(this.filterTimeoutId);
            this.filterTimeoutId = setTimeout(() => {
                this.setFilter();
            }, 100);
        } else this.getOptions();

        this.isOpen = !this.isOpen;
    }

    handleClickOutside(event: any) {
        if (!this.buttonTarget.nativeElement.contains(event as Element)) {
            if (this.isOpen) {
                if (!!this.filterTimeoutId) clearTimeout(this.filterTimeoutId);
                this.filterTimeoutId = setTimeout(() => {
                    this.setFilter();
                }, 100);
            }
            this.isOpen = false;
        }
    }

    handleOptionClick(event: PointerEvent, option: GeneralOption): void {
        event.stopPropagation();
        if (!option) {
            return;
        }
        if (this.selectControl.value.includes(option.label)) {
            this.selectedOptions.push(option);
        } else {
            this.selectedOptions = this.selectedOptions.filter((item) => item.value !== option.value);
        }
    }

    toggleSelectAll(event: PointerEvent): void {
        event.stopPropagation();
        if (this.selectControl.value.length >= this.options.length) {
            this.selectControl.reset([]);
            this.isAllSelected = false;
        } else {
            let _options = this.options.reduce((acc, curr) => {
                return [...acc, curr.value];
            }, []);
            this.selectControl.setValue([..._options, 0]);
            this.isAllSelected = true;
        }
    }

    protected filterOptions() {
        if (!this.options) {
            return;
        }

        let search = this.filterControl.value;
        if (!search) {
            if (!this.dataFunction) {
                this.filteredOptions$.next(this.createOptionsArray(this.selectedOptions, this.options));
                this.selectControl.setValue(this.selectedOptions.map((option) => option.value));
            } else this.getOptions();

            return;
        } else {
            search = search.toLowerCase();
        }

        if (!this.dataFunction) {
            this.filteredOptions$.next(
                this.createOptionsArray(
                    this.selectedOptions,
                    this.options.filter((opt) => opt.label.toLowerCase().indexOf(search) > -1),
                ),
            );
            this.selectControl.setValue(this.selectedOptions.map((option) => option.value));
        } else {
            this.isLoading$.next(true);
            this.dataFunction(this.fieldName, search)
                .pipe(take(1))
                .subscribe((data) => {
                    const _options = data.response.data.map((item) => {
                        return {value: item.key, label: item.name};
                    });
                    const _mergedOptions = this.createOptionsArray(this.selectedOptions, _options);
                    this.options = _mergedOptions;
                    this.filteredOptions$.next(_mergedOptions);
                    this.selectControl.setValue(this.selectedOptions.map((option) => option.value));
                    this.isLoading$.next(false);
                });
        }
    }

    createOptionsArray(selectedOptions, options): any[] {
        const mergedArray = [...selectedOptions, ...options];
        const result = mergedArray.filter((obj, index) => {
            return mergedArray.findIndex((dupObj) => JSON.stringify(dupObj) === JSON.stringify(obj)) === index;
        });
        return result;
    }

    getLabels(values: string[]) {
        let _labels = values?.reduce((acc, curr) => {
            if (acc !== '') acc += ', ';
            return (acc += this.options.find((option) => option.value === curr)?.label);
        }, '');
        return _labels || '';
    }

    // Get length, but without the 'All' option counted as an item
    getLengthSelected(options: any[]) {
        return options.filter((o) => o != 0 && o != '' && o != 'All').length;
    }

    getOptions() {
        if (this.dataFunction) {
            this.isLoading$.next(true);

            this.dataFunction(this.fieldName, this.filterControl.value || '').subscribe((data) => {
                const _options = data.response.data.map((item) => {
                    return {value: item.key, label: item.name};
                });
                const _mergedOptions = this.createOptionsArray(this.selectedOptions, _options);
                this.options = _mergedOptions;
                this.filteredOptions$.next(_mergedOptions);
                this.selectControl.setValue(this.selectedOptions.map((option) => option.value));
                this.isLoading$.next(false);
            });
        }
    }
}
